import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';

// 管理者の「利用状況」タブ（2026-09-19）
// 学校ごとの使われ方の集計と、子どもが書いたふりかえりの一覧を見る。
// 時刻はサーバー側で日本時間に直して返している。

const P = '#3c3489';
const S = {
  card: { background: '#fff', border: '1px solid #d0d8e8', borderRadius: 12, padding: 20, marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 14 },
  sub: { fontSize: 12, color: '#888', fontWeight: 400, marginLeft: 8 },
  toolbar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '8px 10px', border: '1px solid #d0d8e8', borderRadius: 8, fontSize: 14, background: '#fff' },
  input: { padding: '8px 10px', border: '1px solid #d0d8e8', borderRadius: 8, fontSize: 14, minWidth: 160 },
  btn: { padding: '8px 14px', border: '1px solid #d0d8e8', borderRadius: 8, background: '#fff', color: '#555', fontSize: 13, cursor: 'pointer' },
  btnPrimary: { padding: '8px 16px', border: 'none', borderRadius: 8, background: P, color: '#fff', fontSize: 13, cursor: 'pointer' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 },
  stat: { background: '#f5f7fb', borderRadius: 10, padding: '12px 14px' },
  statLabel: { fontSize: 12, color: '#777' },
  statValue: { fontSize: 22, fontWeight: 700, color: P, marginTop: 4 },
  statNote: { fontSize: 12, color: '#888', marginTop: 2 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: '#f0eefe', color: P, fontWeight: 600, borderBottom: '1px solid #d0d8e8', whiteSpace: 'nowrap' },
  td: { padding: '7px 10px', borderBottom: '1px solid #f0f2f5', verticalAlign: 'middle' },
  hint: { fontSize: 13, color: '#555', lineHeight: 1.8 },
  warn: { color: '#b45309', fontWeight: 600 },
  chip: { display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#f0eefe', color: P, marginRight: 4, marginBottom: 2 },
  goalChip: { display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#e1f5ee', color: '#085041', marginRight: 4 },
  review: { borderBottom: '1px solid #eef0f4', padding: '14px 2px' },
  reviewHead: { fontSize: 12, color: '#777', marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  reviewBody: { fontSize: 14, color: '#222', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  ai: { fontSize: 12, color: '#555', background: '#f7f7fb', borderRadius: 8, padding: '8px 12px', marginTop: 8, lineHeight: 1.7, whiteSpace: 'pre-wrap' },
  empty: { fontSize: 13, color: '#999', padding: '12px 0' },
};

const DOW = ['', '月', '火', '水', '木', '金', '土', '日'];
const LEN_LABELS = ['20字未満', '20〜49字', '50〜99字', '100〜199字', '200字以上'];
const HOUR_BANDS = [['〜9時（朝）', 0, 8], ['9〜12時（午前）', 9, 11], ['12時台（昼）', 12, 12], ['13〜16時（午後）', 13, 15], ['16時〜（放課後・家）', 16, 23]];
const COUNT_BANDS = [['0回', 0, 0], ['1回', 1, 1], ['2〜3回', 2, 3], ['4〜5回', 4, 5], ['6〜9回', 6, 9], ['10回以上', 10, 1e9]];

const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0);
const who = (r) => `${r.grade}年${r.class_name} ${r.number}番`;

// 横棒グラフ。items: [{ label, value, note }]
function Bars({ items, color = P, unit = '件' }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length) return <div style={S.empty}>まだデータがありません</div>;
  return (
    <div>
      {items.map((i) => (
        <div key={i.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
          <div style={{ width: 118, flexShrink: 0, color: '#444' }}>{i.label}</div>
          <div style={{ flex: 1, background: '#f0f2f5', borderRadius: 4, height: 14, overflow: 'hidden' }}>
            <div style={{ width: `${(100 * i.value) / max}%`, background: color, height: '100%' }} />
          </div>
          <div style={{ width: 70, textAlign: 'right', color: '#333' }}>
            {i.value}{unit}{i.note && <span style={{ color: '#999', fontSize: 11 }}> {i.note}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// 月ごとの縦棒グラフ
function MonthBars({ months }) {
  const max = Math.max(1, ...months.map((m) => m.reviews));
  if (!months.length) return <div style={S.empty}>まだデータがありません</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 170, minWidth: months.length * 52, paddingTop: 10 }}>
        {months.map((m) => (
          <div key={m.month} style={{ flex: 1, minWidth: 42, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#333', marginBottom: 3 }}>{m.reviews}</div>
            <div style={{ height: `${(120 * m.reviews) / max}px`, background: m.reviews ? P : '#e5e7eb', borderRadius: '4px 4px 0 0', minHeight: 2 }} />
            <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>{Number(m.month.slice(5))}月</div>
            <div style={{ fontSize: 10, color: '#aaa' }}>{m.active_students}人</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UsageTab() {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('number');

  const emptyFilters = { student_id: '', subject: '', emotion: '', q: '' };
  const [filters, setFilters] = useState(emptyFilters);
  const [qInput, setQInput] = useState('');
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const listRef = useRef(null);

  // 学校一覧。初期表示は児童の多い学校（体験校・テスト校より実際の学校を先に）
  useEffect(() => {
    api.get('/admin/schools').then((r) => {
      const list = [...r.data].sort((a, b) => Number(b.student_count) - Number(a.student_count));
      setSchools(list);
      const first = list.find((s) => !/^(DEMO|TEST)/.test(s.school_code)) || list[0];
      if (first) setSchoolId(String(first.id));
    }).catch(() => setError('学校一覧の取得に失敗しました'));
  }, []);

  const loadUsage = useCallback(() => {
    if (!schoolId) return;
    setLoading(true); setError('');
    api.get(`/admin/schools/${schoolId}/usage`, { params: { class_id: classId || undefined } })
      .then((r) => setData(r.data))
      .catch(() => setError('利用状況の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [schoolId, classId]);

  const loadReviews = useCallback((offset = 0) => {
    if (!schoolId) return;
    setLoadingReviews(true);
    const params = { class_id: classId || undefined, limit: 30, offset };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get(`/admin/schools/${schoolId}/reviews`, { params })
      .then((r) => {
        setTotal(r.data.total);
        setReviews((prev) => (offset ? [...prev, ...r.data.rows] : r.data.rows));
      })
      .catch(() => setError('ふりかえりの取得に失敗しました'))
      .finally(() => setLoadingReviews(false));
  }, [schoolId, classId, filters]);

  useEffect(() => { loadUsage(); }, [loadUsage]);
  useEffect(() => { loadReviews(0); }, [loadReviews]);

  const changeSchool = (id) => { setSchoolId(id); setClassId(''); setFilters(emptyFilters); setQInput(''); };
  const changeClass = (id) => { setClassId(id); setFilters(emptyFilters); setQInput(''); };
  const showStudent = (studentId) => {
    setFilters({ ...emptyFilters, student_id: String(studentId) }); setQInput('');
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const s = data?.summary;
  const students = data ? [...data.students].sort((a, b) =>
    sortKey === 'many' ? b.reviews - a.reviews : sortKey === 'few' ? a.reviews - b.reviews : 0) : [];
  const notYet = data ? data.students.filter((x) => x.reviews === 0).length : 0;
  const hours = data ? HOUR_BANDS.map(([label, from, to]) => ({
    label, value: data.hours.filter((h) => h.hour >= from && h.hour <= to).reduce((a, h) => a + h.count, 0) })) : [];
  const countDist = data ? COUNT_BANDS.map(([label, from, to]) => ({
    label, value: data.students.filter((x) => x.reviews >= from && x.reviews <= to).length })) : [];
  const selectedStudent = data?.students.find((x) => String(x.student_id) === filters.student_id);

  return (
    <div>
      <div style={S.card}>
        <div style={S.toolbar}>
          <select style={S.select} value={schoolId} onChange={(e) => changeSchool(e.target.value)}>
            {schools.map((sc) => (
              <option key={sc.id} value={sc.id}>{sc.school_name}（{sc.school_code}・児童{sc.student_count}人）</option>
            ))}
          </select>
          <select style={S.select} value={classId} onChange={(e) => changeClass(e.target.value)}>
            <option value="">すべてのクラス</option>
            {data?.class_list.map((c) => <option key={c.id} value={c.id}>{c.grade}年{c.class_name}</option>)}
          </select>
          <button style={S.btn} onClick={() => { loadUsage(); loadReviews(0); }}>更新</button>
          {loading && <span style={{ fontSize: 12, color: '#888' }}>読み込み中…</span>}
        </div>
        {error && <div style={{ color: '#e24b4a', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {s && (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>概要<span style={S.sub}>{data.school.school_name}{s.first_at ? `／${s.first_at} から` : ''}</span></div>
            <div style={S.stats}>
              <div style={S.stat}><div style={S.statLabel}>登録している児童</div><div style={S.statValue}>{s.students}人</div></div>
              <div style={S.stat}><div style={S.statLabel}>書いたことのある児童</div><div style={S.statValue}>{s.active_students}人</div><div style={S.statNote}>参加率 {pct(s.active_students, s.students)}%</div></div>
              <div style={S.stat}><div style={S.statLabel}>ふりかえり</div><div style={S.statValue}>{s.reviews}件</div><div style={S.statNote}>直近7日 {s.reviews_7d}件</div></div>
              <div style={S.stat}><div style={S.statLabel}>最後の提出</div><div style={{ ...S.statValue, fontSize: 16 }}>{s.last_at || '—'}</div></div>
              <div style={S.stat}><div style={S.statLabel}>平均の文字数</div><div style={S.statValue}>{s.avg_len ?? '—'}{s.avg_len != null && '字'}</div></div>
              <div style={S.stat}><div style={S.statLabel}>目標を選んで書いた</div><div style={S.statValue}>{s.with_goal}件</div><div style={S.statNote}>全体の {pct(s.with_goal, s.reviews)}%</div></div>
            </div>
            <div style={{ ...S.hint, marginTop: 14 }}>
              {notYet > 0 && <div>・まだ一度も書いていない児童：<span style={S.warn}>{notYet}人</span></div>}
              {data.classes.filter((c) => c.reviews === 0).map((c) => (
                <div key={c.class_id}>・<span style={S.warn}>{c.grade}年{c.class_name}</span> はまだ使われていません</div>
              ))}
              <div>・気持ちを選ばずに書いたもの：{s.no_emotion}件（{pct(s.no_emotion, s.reviews)}%）</div>
              {s.ai_missing > 0 && <div>・AIのコメントが付いていないもの：{s.ai_missing}件</div>}
              <div>・先生の準備：教科・単元 {data.teacher.subjects}件／目標 {data.teacher.goals}件{data.teacher.last_subject_at ? `（最後の登録 ${data.teacher.last_subject_at}）` : ''}</div>
              <div style={{ color: '#999', fontSize: 12 }}>※「目標を選んで書いた」は 2026年9月19日から記録しています（それ以前は保存されていませんでした）</div>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>クラス別</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>クラス</th><th style={S.th}>児童</th><th style={S.th}>書いた児童</th>
                  <th style={{ ...S.th, minWidth: 140 }}>参加率</th><th style={S.th}>ふりかえり</th><th style={S.th}>最後の提出</th>
                </tr></thead>
                <tbody>
                  {data.classes.map((c) => (
                    <tr key={c.class_id}>
                      <td style={S.td}>{c.grade}年{c.class_name}</td>
                      <td style={S.td}>{c.students}人</td>
                      <td style={S.td}>{c.active_students}人</td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, background: '#f0f2f5', borderRadius: 4, height: 10 }}>
                            <div style={{ width: `${pct(c.active_students, c.students)}%`, background: P, height: '100%', borderRadius: 4 }} />
                          </div>
                          <span>{pct(c.active_students, c.students)}%</span>
                        </div>
                      </td>
                      <td style={S.td}>{c.reviews}件</td>
                      <td style={S.td}>{c.last_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>月ごとの提出<span style={S.sub}>棒の下の「人」はその月に書いた児童の数</span></div>
            <MonthBars months={data.months} />
          </div>

          <div style={S.grid2}>
            <div style={S.card}>
              <div style={S.cardTitle}>教科<span style={S.sub}>（ ）は平均文字数</span></div>
              <Bars items={data.subjects.map((x) => ({ label: x.name, value: x.reviews, note: `(${x.avg_len}字)` }))} />
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>気持ち<span style={S.sub}>1件で複数選べる</span></div>
              <Bars items={data.emotions.map((x) => ({ label: x.tag, value: x.count }))} color="#1d9e75" />
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>書いた時間帯</div>
              <Bars items={hours} color="#7c6fd6" />
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>曜日</div>
              <Bars items={data.weekdays.map((x) => ({ label: `${DOW[x.dow]}曜日`, value: x.count }))} color="#7c6fd6" />
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>文字数</div>
              <Bars items={data.lengths.map((x) => ({ label: LEN_LABELS[x.bucket], value: x.count }))} color="#d97706" />
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>1人あたりの回数</div>
              <Bars items={countDist} color="#d97706" unit="人" />
            </div>
          </div>

          <div style={S.card}>
            <div style={{ ...S.toolbar, justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ ...S.cardTitle, marginBottom: 0 }}>児童ごとの状況<span style={S.sub}>{data.students.length}人</span></div>
              <select style={S.select} value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                <option value="number">番号順</option>
                <option value="many">回数の多い順</option>
                <option value="few">回数の少ない順</option>
              </select>
            </div>
            <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #f0f2f5', borderRadius: 8 }}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>児童</th><th style={S.th}>書いた回数</th><th style={S.th}>最後に書いた日</th><th style={S.th}>平均の文字数</th><th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {students.map((x) => (
                    <tr key={x.student_id} style={x.reviews === 0 ? { color: '#aaa' } : {}}>
                      <td style={S.td}>{who(x)}</td>
                      <td style={S.td}>{x.reviews}回</td>
                      <td style={S.td}>{x.last_at || '—'}</td>
                      <td style={S.td}>{x.avg_len != null ? `${x.avg_len}字` : '—'}</td>
                      <td style={S.td}>{x.reviews > 0 && <button style={S.btn} onClick={() => showStudent(x.student_id)}>見る</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div style={S.card} ref={listRef}>
        <div style={S.cardTitle}>子どもが書いたふりかえり<span style={S.sub}>新しい順・{total}件</span></div>
        <div style={{ ...S.toolbar, marginBottom: 8 }}>
          <select style={S.select} value={filters.student_id} onChange={(e) => setFilters({ ...filters, student_id: e.target.value })}>
            <option value="">すべての児童</option>
            {data?.students.filter((x) => x.reviews > 0).map((x) => (
              <option key={x.student_id} value={x.student_id}>{who(x)}（{x.reviews}件）</option>
            ))}
          </select>
          <select style={S.select} value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })}>
            <option value="">すべての教科</option>
            {data?.subjects.map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
          </select>
          <select style={S.select} value={filters.emotion} onChange={(e) => setFilters({ ...filters, emotion: e.target.value })}>
            <option value="">すべての気持ち</option>
            {data?.emotions.map((x) => <option key={x.tag} value={x.tag}>{x.tag}</option>)}
          </select>
          <input style={S.input} placeholder="言葉でさがす" value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setFilters({ ...filters, q: qInput.trim() }); }} />
          <button style={S.btnPrimary} onClick={() => setFilters({ ...filters, q: qInput.trim() })}>さがす</button>
          {Object.values(filters).some(Boolean) && (
            <button style={S.btn} onClick={() => { setFilters(emptyFilters); setQInput(''); }}>絞り込みを解除</button>
          )}
        </div>
        {selectedStudent && <div style={{ fontSize: 13, color: P, marginBottom: 4 }}>{who(selectedStudent)} のふりかえり</div>}

        {reviews.length === 0 && !loadingReviews && <div style={S.empty}>該当するふりかえりはありません</div>}
        {reviews.map((r) => (
          <div key={r.id} style={S.review}>
            <div style={S.reviewHead}>
              <span>{r.at}</span>
              <span style={{ color: '#333', fontWeight: 600 }}>{who(r)}</span>
              <span>{r.subject_name}{r.unit_name ? `（${r.unit_name}）` : ''}</span>
              {r.goal_text && <span style={S.goalChip}>目標：{r.goal_text}</span>}
              {(Array.isArray(r.emotion_tags) ? r.emotion_tags : []).map((t) => <span key={t} style={S.chip}>{t}</span>)}
            </div>
            <div style={S.reviewBody}>{r.content}</div>
            {r.ai_comment && (
              <details>
                <summary style={{ fontSize: 12, color: '#888', cursor: 'pointer', marginTop: 6 }}>AIのコメントを見る</summary>
                <div style={S.ai}>{r.ai_comment}</div>
              </details>
            )}
          </div>
        ))}
        {reviews.length < total && (
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <button style={S.btn} disabled={loadingReviews} onClick={() => loadReviews(reviews.length)}>
              {loadingReviews ? '読み込み中…' : `さらに表示（残り ${total - reviews.length}件）`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
